// GAVION team sessions (owner request 2026-10-15). Demo-mode / FakeRest mirror of private.session_json,
// private.team_sessions_at, private.live_sessions_at and private.session_calls_at
// (supabase/schemas/59_gavion_team_sessions.sql). The demo always uses America/Toronto (the SQL default zone).
// Counts come from the session's calls (calls.dial_session_id) with the leaderboard's definitions; a session ends at
// sessionEnd (mirror of private.dial_session_end, 48_gavion_rep_stats.sql).
import type {
  LiveSession,
  SessionCall,
  SessionDetail,
  SessionSummary,
  TeamSessionsReport,
} from "../../reports/types";
import { periodBounds } from "../funnel/period";
import { CONTACT_OUTCOMES } from "../funnel/prospectingFunnel";
import { round } from "../funnel/stats";
import {
  sessionActivity,
  sessionEnd,
  type RepStatsCompany,
  type RepStatsContact,
  type RepStatsMeetingNote,
  type RepStatsSale,
} from "../repStats/repStats";

export type TeamSession = {
  id: number;
  sales_id: number;
  started_at: string;
  ended_at: string | null;
  paused?: boolean | null;
  mode?: string | null;
};
export type TeamSessionCall = {
  id: number;
  sales_id: number;
  direction: "outbound" | "inbound";
  dialed_at: string;
  claimed_at?: string | null;
  outcome_at?: string | null;
  outcome: string | null;
  dial_session_id?: number | null;
  company_id: number | null;
  contact_id?: number | null;
  billsec_s?: number | null;
  note?: string | null;
};

/** Sessions per page in « Historique » (the RPC accepts 1–100). */
export const SESSIONS_PER_PAGE = 50;

const isDial = (c: TeamSessionCall) =>
  c.direction === "outbound" && c.outcome !== "skipped";
const fullName = (s: {
  first_name?: string | null;
  last_name?: string | null;
}) => `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim();
const iso = (ms: number) => new Date(ms).toISOString();
const perHour = (dials: number, seconds: number) =>
  seconds >= 60 ? round(dials / (seconds / 3600), 1) : null;
const latest = (...xs: (string | null | undefined)[]) => {
  const ms = xs.filter((x): x is string => !!x).map(Date.parse);
  return ms.length ? Math.max(...ms) : null;
};

/** Mirror of private.session_json: rep, times, live flag, whole-second duration and counts from the calls. */
export const sessionSummary = (
  s: TeamSession,
  calls: readonly TeamSessionCall[],
  sales: readonly RepStatsSale[],
  now: Date,
): SessionSummary => {
  const mine = calls.filter((c) => c.dial_session_id === s.id);
  const end = sessionEnd(s, sessionActivity(mine), now.getTime());
  const seconds = Math.max(
    0,
    Math.round((end - Date.parse(s.started_at)) / 1000),
  );
  const dials = mine.filter(isDial);
  const sale = sales.find((x) => x.id === s.sales_id);
  return {
    id: s.id,
    sales_id: s.sales_id,
    name: sale ? fullName(sale) : "",
    mode: s.mode ?? "A",
    paused: !!s.paused,
    started_at: s.started_at,
    ended_at: s.ended_at,
    ends_at: iso(end),
    live: s.ended_at == null && end >= now.getTime(),
    duration_seconds: seconds,
    dials: dials.length,
    conversations: dials.filter((c) => CONTACT_OUTCOMES.has(c.outcome ?? ""))
      .length,
    demos: dials.filter((c) => c.outcome === "demo_booked").length,
    talk_seconds: mine.reduce((t, c) => t + (c.billsec_s ?? 0), 0),
    dials_per_hour: perHour(dials.length, seconds),
  };
};

/** Mirror of private.team_sessions_at: sessions started in the period (one rep or all), newest first, one page. */
export const computeTeamSessions = (input: {
  sessions: readonly TeamSession[];
  calls: readonly TeamSessionCall[];
  sales: readonly RepStatsSale[];
  from: string;
  to: string;
  salesId: number | null;
  limit: number;
  offset: number;
  now: Date;
}): TeamSessionsReport => {
  const { start, end } = periodBounds(input.from, input.to);
  const rows = input.sessions
    .filter((s) => {
      const t = Date.parse(s.started_at);
      return (
        t >= start &&
        t < end &&
        (input.salesId == null || s.sales_id === input.salesId)
      );
    })
    .sort(
      (a, b) =>
        Date.parse(b.started_at) - Date.parse(a.started_at) || b.id - a.id,
    )
    .map((s) => sessionSummary(s, input.calls, input.sales, input.now));
  const sum = (k: "duration_seconds" | "dials" | "conversations" | "demos") =>
    rows.reduce((t, r) => t + r[k], 0);
  return {
    from: input.from,
    to: input.to,
    timezone: "America/Toronto",
    sales_id: input.salesId,
    total: rows.length,
    totals: {
      sessions: rows.length,
      duration_seconds: sum("duration_seconds"),
      dials: sum("dials"),
      conversations: sum("conversations"),
      demos: sum("demos"),
      dials_per_hour: perHour(sum("dials"), sum("duration_seconds")),
    },
    rows: rows.slice(input.offset, input.offset + input.limit),
  };
};

const TWELVE_HOURS = 12 * 3_600_000;

/** Mirror of private.live_sessions_at: live sessions (oldest first) with the rep's current lead. */
export const computeLiveSessions = (input: {
  sessions: readonly TeamSession[];
  calls: readonly TeamSessionCall[];
  sales: readonly RepStatsSale[];
  companies: readonly RepStatsCompany[];
  contacts: readonly RepStatsContact[];
  now: Date;
}): LiveSession[] => {
  const now = input.now.getTime();
  const companies = new Map(input.companies.map((c) => [c.id, c]));
  const contacts = new Map(input.contacts.map((c) => [c.id, c]));
  return input.sessions
    .filter((s) => {
      const t = Date.parse(s.started_at);
      return s.ended_at == null && t <= now && t > now - TWELVE_HOURS;
    })
    .map((s) => ({
      s,
      sum: sessionSummary(s, input.calls, input.sales, input.now),
    }))
    .filter(({ sum }) => sum.live)
    .sort(
      (a, b) =>
        Date.parse(a.s.started_at) - Date.parse(b.s.started_at) ||
        a.s.id - b.s.id,
    )
    .map(({ s, sum }) => {
      const own = input.calls.filter((c) => c.dial_session_id === s.id);
      const last = Math.max(
        Date.parse(s.started_at),
        ...own.map(
          (c) => latest(c.claimed_at, c.dialed_at, c.outcome_at) ?? -Infinity,
        ),
      );
      const open = input.calls
        .filter(
          (k) =>
            k.sales_id === s.sales_id &&
            k.direction === "outbound" &&
            k.outcome == null &&
            (k.dial_session_id === s.id ||
              (k.dial_session_id == null &&
                Date.parse(k.dialed_at) >= Date.parse(s.started_at))),
        )
        .map((k) => ({ k, at: latest(k.claimed_at, k.dialed_at) ?? 0 }))
        .sort((a, b) => b.at - a.at || b.k.id - a.k.id)[0];
      const contact =
        open?.k.contact_id != null
          ? contacts.get(open.k.contact_id)
          : undefined;
      return {
        ...sum,
        last_activity_at: iso(last),
        current_call: open
          ? {
              call_id: open.k.id,
              since: iso(open.at),
              company_id: open.k.company_id,
              company_name:
                open.k.company_id != null
                  ? (companies.get(open.k.company_id)?.name ?? null)
                  : null,
              contact_id: open.k.contact_id ?? null,
              contact_name: contact ? fullName(contact) || null : null,
            }
          : null,
      };
    });
};

/** Mirror of private.session_calls_at: the session and every call of it in time order; null when unknown. */
export const computeSessionCalls = (input: {
  sessionId: number;
  sessions: readonly TeamSession[];
  calls: readonly TeamSessionCall[];
  sales: readonly RepStatsSale[];
  companies: readonly RepStatsCompany[];
  contacts: readonly RepStatsContact[];
  meetingNotes: readonly RepStatsMeetingNote[];
  now: Date;
}): SessionDetail | null => {
  const s = input.sessions.find((x) => x.id === input.sessionId);
  if (!s) return null;
  const companies = new Map(input.companies.map((c) => [c.id, c]));
  const contacts = new Map(input.contacts.map((c) => [c.id, c]));
  const startedMs = (m: RepStatsMeetingNote) =>
    m.started_at ? Date.parse(m.started_at) : -Infinity;
  const calls: SessionCall[] = input.calls
    .filter((c) => c.dial_session_id === s.id)
    .sort(
      (a, b) =>
        Date.parse(a.dialed_at) - Date.parse(b.dialed_at) || a.id - b.id,
    )
    .map((c) => {
      const note = input.meetingNotes
        .filter((m) => m.match_status === "matched" && m.call_id === c.id)
        .sort((a, b) => startedMs(b) - startedMs(a) || b.id - a.id)[0];
      const contact =
        c.contact_id != null ? contacts.get(c.contact_id) : undefined;
      return {
        id: c.id,
        dialed_at: c.dialed_at,
        claimed_at: c.claimed_at ?? null,
        direction: c.direction,
        outcome: c.outcome,
        counts_as_dial: isDial(c),
        company_id: c.company_id,
        company_name:
          c.company_id != null
            ? (companies.get(c.company_id)?.name ?? null)
            : null,
        contact_id: c.contact_id ?? null,
        contact_name: contact ? fullName(contact) || null : null,
        billsec_s: c.billsec_s ?? null,
        note: c.note ?? null,
        meeting_note: note
          ? {
              id: note.id,
              title: note.title ?? null,
              share_url: note.share_url ?? null,
              started_at: note.started_at ?? null,
            }
          : null,
      };
    });
  return {
    session: sessionSummary(s, input.calls, input.sales, input.now),
    calls,
  };
};

// ------------------------------------------------------------------ view helpers

/** Seconds elapsed since `iso` at `now` (never negative): the ticking duration of a live session. */
export const elapsedSeconds = (iso: string, now: Date): number =>
  Math.max(0, Math.floor((now.getTime() - Date.parse(iso)) / 1000));
